import { useReactiveVar } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { EventsMapping } from 'sigma/types';
import { edgesQueryOptions } from '../edges-query';
import { sigmaInstanceVar } from './sigma-options-var';

export const useSigmaHighlightHovered = () => {
  const sigma = useReactiveVar(sigmaInstanceVar);
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const edgeDirection = searchParams.get('dir') === 'out' ? 'out' : 'in';

  const toast = useToast();

  useEffect(() => {
    if (!sigma) return;

    const onEnterNode: EventsMapping['enterNode'] = () => {
      document.body.style.cursor = 'pointer';
    };
    const onLeaveNode: EventsMapping['leaveNode'] = () => {
      document.body.style.cursor = 'default';
    };

    const onDownNode: EventsMapping['downNode'] = (event) => {
      queryClient.prefetchQuery(edgesQueryOptions(event.node, edgeDirection));
    };

    const onSelectNode: EventsMapping['clickNode'] = (event) => {
      setSearchParams((prev) => {
        prev.set('node', event.node);
        return prev;
      });
    };
    const onStageClick: EventsMapping['clickStage'] = () => {
      setSearchParams((prev) => {
        if (prev.has('node')) {
          prev.delete('node');
        }
        return prev;
      });
    };

    sigma.on('downNode', onDownNode);
    sigma.on('clickNode', onSelectNode);
    sigma.on('clickStage', onStageClick);
    sigma.on('enterNode', onEnterNode);
    sigma.on('leaveNode', onLeaveNode);

    return () => {
      sigma.off('downNode', onDownNode);
      sigma.off('clickNode', onSelectNode);
      sigma.off('clickStage', onStageClick);
      sigma.off('enterNode', onEnterNode);
      sigma.off('leaveNode', onLeaveNode);
    };
  }, [setSearchParams, sigma, toast, queryClient, edgeDirection]);
};
